import React, { FC } from 'react'
import { Routes, Route } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CustomLayer, PointStyle, LineStyle, PolygonStyle } from 'models/custom-layer'
import { GeometryTypes } from 'models/layer'
import { Card } from 'components/Card'
import { Stack } from 'components/Stack'
import { Flex } from 'components/Flex'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { Table, TableHead, TableRow, TableHeadCell, TableBody, TableCell } from 'components/Table'
import { Icon } from 'components/Icon'
import { IconUtils } from 'utils/icon'
import { useFetchCustomLayers } from 'services/custom-layer'
import { CustomLayerDialog } from './components/CustomLayerDialog'
import { ButtonLink } from 'components/ButtonLink'
import { DeleteCustomLayerDialog } from './components/DeleteCustomLayerDialog'
import { CustomLayerStylesDialog } from './components/CustomLayerStylesDialog'
import { Spinner } from 'components/Spinner'
import styles from './CustomLayersCard.module.scss'

export const CustomLayersCard: FC = (_props) => {
  const { t } = useTranslation()
  const { customLayers = [], status } = useFetchCustomLayers()

  const getLayerIcon = (layer: CustomLayer) => {
    switch (layer.geometryType) {
      case GeometryTypes.Point:
        const pointstyle = layer.viz as PointStyle
        try {
          return (
            <Icon
              name={IconUtils.getIconFromName(pointstyle.layout['icon-image'])}
              style={{ color: pointstyle.paint['icon-color'] }}
              className={styles.geometryIcon}
            />
          )
        } catch (error) {
          return (
            <label>NO ICON</label>
          )
        }
      case GeometryTypes.Line:
        const linestyle = layer.viz as LineStyle
        return (
          <Icon
            name="separator"
            style={{ color: linestyle.paint['line-color'] }}
            className={styles.geometryIcon}
          />
        )
      case GeometryTypes.Polygon:
        const polygonstyle = layer.viz as PolygonStyle
        return (
          <Icon
            name="shape-fill"
            style={{ color: polygonstyle.paint['fill-color'] }}
            className={styles.geometryIcon}
          />
        )
    }
  }

  return (
    <>
      <Card className={styles.customLayersCard}>
        <Stack space="xlarge">
          <Flex justify="between">
            <Stack space="xsmall">
              <Heading size="secondary">{t('datasets.layers.title')}</Heading>
              <Text>{t('datasets.layers.desc')}</Text>
            </Stack>
            <ButtonLink to="./custom-layers/new">{t('datasets.layers.add')}</ButtonLink>
          </Flex>
          <Table className={styles.table}>
            <TableHead>
              <TableRow>
                <TableHeadCell>
                  <Text weight="strong" align="left">
                    {t('datasets.layers.table.name')}
                  </Text>
                </TableHeadCell>
                <TableHeadCell>
                  <Text weight="strong" align="left">
                    {t('datasets.layers.table.file')}
                  </Text>
                </TableHeadCell>
                <TableHeadCell>
                  <Text weight="strong" align="left">
                    {t('datasets.layers.table.style')}
                  </Text>
                </TableHeadCell>
                <TableHeadCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {customLayers.map((layer) => (
                <TableRow key={layer.id}>
                  <TableCell>
                    <Text weight="strong">{layer.name}</Text>
                  </TableCell>
                  <TableCell>
                    <a href={layer.file.url} className={styles.downloadLink} download>
                      <Text weight="strong" className={styles.fileName} component="span">
                        {layer.file.name}
                      </Text>
                      <Icon name="download-line" />
                    </a>
                  </TableCell>
                  <TableCell>
                    <Flex justify="start">
                      {getLayerIcon(layer)}
                      <Text>{t(`datasets.layers.geometry.${layer.geometryType}`)}</Text>
                    </Flex>
                  </TableCell>
                  <TableCell>
                    <Flex justify="end" gap="sm">
                      <ButtonLink
                        size="small"
                        variant="secondary"
                        to={`./custom-layers/${layer.id}/edit`}
                      >
                        {t('datasets.edit')}
                      </ButtonLink>
                      <ButtonLink
                        size="small"
                        variant="danger"
                        to={`./custom-layers/${layer.id}/delete`}
                      >
                        {t('datasets.delete')}
                      </ButtonLink>
                    </Flex>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Stack>

        {status === 'loading' && (
          <Flex justify="center" align="center" className={styles.loading}>
            <Spinner />
          </Flex>
        )}

        {status === 'success' && customLayers.length === 0 && (
          <Flex justify="center" className={styles.placeholder}>
            <Stack space="medium">
              <Stack space="small">
                <Icon name="eye-close-line" />
                <Text>{t('datasets.layers.placeholder')}</Text>
              </Stack>
              <ButtonLink to="./custom-layers/new">{t('datasets.layers.add')}</ButtonLink>
            </Stack>
          </Flex>
        )}
      </Card>

      <Routes>
        <Route path="/custom-layers/new" element={<CustomLayerDialog />} />
        <Route path="/custom-layers/:layerId/edit" element={<CustomLayerDialog />} />
        <Route path="/custom-layers/:layerId/edit-styles" element={<CustomLayerStylesDialog />} />
        <Route path="/custom-layers/:layerId/delete" element={<DeleteCustomLayerDialog />} />
      </Routes>
    </>
  )
}

CustomLayersCard.displayName = 'CustomLayersCard'
